/**
 * Phase 23 (Plan 23-02) — MyWorkOrdersWidget.
 *
 * Up to 5 in-scope WOs sorted by (Status==Active desc, StartDate asc) — the
 * caller's daily focus list. Each row links to /work-orders/{id}.
 */

import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import {
  WidgetCard,
  WidgetEmpty,
  WidgetError,
  WidgetSkeleton,
} from '@/components/dashboard/widget-states';
import type { ApiError } from '@/lib/api/error';
import type { MyWorkOrdersBlock } from '@/types/dashboard';

export interface MyWorkOrdersWidgetProps {
  readonly data: MyWorkOrdersBlock | null | undefined;
  readonly isLoading: boolean;
  readonly error: ApiError | null;
  readonly onRetry: () => void;
  readonly className?: string;
}

interface BodyProps {
  readonly data: MyWorkOrdersBlock;
}

function MyWorkOrdersBody({ data }: BodyProps): JSX.Element {
  return (
    <ul className="space-y-2" data-testid="mywos-list">
      {data.items.map((wo) => (
        <li key={wo.id}>
          <Link
            to={`/work-orders/${String(wo.id)}`}
            className="hover:bg-accent/50 flex flex-col gap-1 rounded-md px-3 py-2 text-sm"
            data-testid={`mywos-row-${String(wo.id)}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{wo.woNumber}</span>
              <span className="text-muted-foreground text-xs">{wo.status}</span>
            </div>
            <div className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
              <span className="truncate">{wo.clientName}</span>
              <span>
                {wo.startDate} → {wo.endDate}
              </span>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function MyWorkOrdersWidget({
  data,
  isLoading,
  error,
  onRetry,
  className,
}: MyWorkOrdersWidgetProps): JSX.Element {
  return (
    <WidgetCard
      title="My Work Orders"
      testId="mywos-widget"
      {...(className !== undefined ? { className } : {})}
    >
      {isLoading ? (
        <WidgetSkeleton testId="mywos-skeleton" rows={3} />
      ) : error ? (
        <WidgetError error={error} onRetry={onRetry} testId="mywos-error" />
      ) : !data || data.items.length === 0 ? (
        <WidgetEmpty
          message="No work orders in your scope."
          testId="mywos-empty"
        />
      ) : (
        <MyWorkOrdersBody data={data} />
      )}
    </WidgetCard>
  );
}
